import React, { useReducer, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { userAppData } from '../MainApp.js';
import { StandardSingleLineTextEntry } from '../common/formComponents/StandardSingleLineTextEntry.js';
import { StandardMultiLineTextEntry } from '../common/formComponents/StandardMultiLineTextEntry.js';
import { StandardDateSelector } from '../common/formComponents/StandardDateSelector.js';
import { StandardNumberInput } from '../common/formComponents/StandardNumberInput.js';
import { StandardRadioButtonSelector } from '../common/formComponents/StandardRadioButtonSelector.js';
import LinksComponent from '../common/formComponents/LinksComponent';
import useS3FileUpload from '../hooks/useS3FileUpload';
import {
    FileDropzone,
    SubmitButton,
} from '../common/formComponents.js';
import { SEC_DataApiPost } from '../func.js';
import Header from '../common/header.js';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../common/requestForm.css';

const paperSizeOptions = ['A4', 'A3', 'A5'];
const sideOptions = [
    { label: 'Single Sided', value: 'singleSided' },
    { label: 'Double Sided', value: 'doubleSided' },
];
const printColourOptions = [
    { label: 'Black & White', value: 'blackWhite' },
    { label: 'Colour', value: 'colour' },
];
const materialOptions = ['Paper', 'Card'];
const paperColourOptions = ['White', 'Blue', 'Pink', 'Yellow'];
const stapleOptions = [
    { label: 'Corner - Single', value: 'cornerSingle' },
    { label: 'Edges - Double', value: 'edgesDouble' },
    { label: 'No Staples', value: 'none' },
];
const bindingOptions = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
];

const initialFormState = {
    subject: '',
    requiredDate: '',
    copies: '1',
    paperSize: 'A4',
    sides: '',
    printColour: 'blackWhite',
    material: 'Paper',
    paperColour: 'White',
    staples: 'none',
    binding: 'no',
    otherInstructions: '',
    files: [],
    links: [],
    rawLinks: [],
    errors: {},
    isSubmitting: false,
    resetKey: 0,
};

/**
 * Stores all Reprographics form UI state without performing API submission.
 */
function formReducer(state, action) {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        case 'SET_ERROR':
            return { ...state, errors: { ...state.errors, [action.field]: action.error } };
        case 'CLEAR_ERRORS':
            return { ...state, errors: {} };
        case 'SET_SUBMITTING':
            return { ...state, isSubmitting: action.value };
        case 'RESET_FORM':
            return { ...initialFormState, resetKey: state.resetKey + 1 };
        default:
            return state;
    }
}

export default function ReproForm() {
    const [state, dispatch] = useReducer(formReducer, initialFormState);
    const [submitStatus, setSubmitStatus] = useState('');
    const [successModalState, setSuccessModalState] = useState({
        active: false,
        requestId: '',
        message: '',
    });
    const { uploadFiles } = useS3FileUpload('repro');

    /**
     * Validate the form fields that must be present before files are uploaded.
     */
    function validateForm() {
        const errors = {};
        if (!state.subject?.trim()) {
            errors.subject = true;
        }
        if (!state.requiredDate || state.requiredDate === '0000-00-00') {
            errors.requiredDate = true;
        }
        const copies = Number(state.copies);
        if (!Number.isFinite(copies) || copies < 1) {
            errors.copies = true;
        }
        const hasIncompleteLinks = state.rawLinks.some(link =>
            (link.name && link.name.trim() !== '' && (!link.url || link.url.trim() === '')) ||
            (link.url && link.url.trim() !== '' && (!link.name || link.name.trim() === ''))
        );
        if (hasIncompleteLinks) {
            errors.links = true;
        }
        return errors;
    }

    /**
     * Uploads attached files, then submits the Reprographics request to the API.
     */
    async function handleSubmit(event) {
        event.preventDefault();
        dispatch({ type: 'CLEAR_ERRORS' });
        dispatch({ type: 'SET_SUBMITTING', value: true });

        const errors = validateForm();
        if (errors.links && Object.keys(errors).length === 1) {
            toast.error('⚠️ Each link must include both a name and a URL', {
                autoClose: 5000,
                position: 'top-right',
            });
            dispatch({ type: 'SET_ERROR', field: 'form', error: errors });
            dispatch({ type: 'SET_SUBMITTING', value: false });
            setSubmitStatus('');
            return;
        }
        if (Object.keys(errors).length > 0) {
            const fieldLabels = {
                subject: 'Subject',
                requiredDate: 'Date Required',
                copies: 'No. of Copies',
                links: 'Links',
            };
            const missingFields = Object.keys(errors)
                .map((field) => fieldLabels[field] || field)
                .join('\n');

            toast.error(`The following fields need checking:\n${missingFields}`, {
                autoClose: 6000,
                position: 'top-right',
                style: { whiteSpace: 'pre-line' },
            });
            dispatch({ type: 'SET_ERROR', field: 'form', error: errors });
            dispatch({ type: 'SET_SUBMITTING', value: false });
            setSubmitStatus('');
            return;
        }

        const timestamp = new Date().toISOString();
        const eventObject = {
            primaryEvent: {
                user: userAppData.cogId,
                timestamp,
                eventData: 'Reprographics request submitted',
            },
            additionalEvents: [],
        };

        const formObj = {
            subject: state.subject.trim(),
            requiredDate: state.requiredDate,
            copies: Number(state.copies),
            paperSize: state.paperSize,
            sides: state.sides || 'doubleSided',
            printColour: state.printColour,
            material: state.material,
            paperColour: state.paperColour,
            staples: state.staples,
            binding: state.binding,
            otherInstructions: state.otherInstructions,
            events: eventObject,
            fileNames: [],
            uploadGroupId: null,
            links: state.links,
        };

        try {
            if (state.files.length > 0) {
                setSubmitStatus('Uploading files...');
                const uploadGroupId = uuidv4();
                const uploaded = await uploadFiles(state.files, userAppData.cogId, { uploadGroupId });
                formObj.fileNames = uploaded;
                formObj.uploadGroupId = uploadGroupId;
            }

            setSubmitStatus('Submitting request...');
            const response = await SEC_DataApiPost('repro', JSON.stringify(formObj), {}, () => {
                throw new Error('Database submission failed');
            });

            const rid = response?.requestId;
            if (!rid || rid === 'error') {
                throw new Error('Invalid or missing ID in response');
            }

            window.scrollTo({ top: 0, behavior: 'smooth' });

            const successModalSettings = response?.successModalSettings || {};
            if (successModalSettings.displaySuccessModal === true) {
                setSuccessModalState({
                    active: true,
                    requestId: rid,
                    message: successModalSettings.successModalText || '',
                });
            } else {
                toast.success(`✅ Reprographics request submitted with ID ${rid}`, {
                    autoClose: 6000,
                    position: 'top-right',
                });
            }
            dispatch({ type: 'RESET_FORM' });
        } catch (err) {
            console.error('Reprographics submission error:', err);
            toast.error('❌ Failed to submit request. Please try again.', {
                autoClose: 5000,
                position: 'top-right',
            });
        } finally {
            dispatch({ type: 'SET_SUBMITTING', value: false });
            setSubmitStatus('');
        }
    }

    return (
        <div className='internal_main_div'>
            <Header title='Reprographics Request Form' subtitle='Submit a new Reprographics request.' />
            <ReproSuccessModal
                active={successModalState.active}
                requestId={successModalState.requestId}
                message={successModalState.message}
                onClose={() => setSuccessModalState({ active: false, requestId: '', message: '' })}
            />
            <div className='formDiv request-form-shell'>
                <form onSubmit={handleSubmit}>
                    <div className='request-form-section'>
                        <h2 className='request-form-section-title'>Request Details</h2>
                        <StandardSingleLineTextEntry
                            title='Subject'
                            data={state.subject}
                            setData={(value) => dispatch({ type: 'SET_FIELD', field: 'subject', value })}
                            errorText={state.errors.subject}
                        />
                        <StandardDateSelector
                            title='Date Required'
                            value={state.requiredDate}
                            setData={(value) => dispatch({ type: 'SET_FIELD', field: 'requiredDate', value })}
                            allowPast={false}
                            layout='inline'
                            errorText={state.errors.requiredDate}
                        />
                        <StandardNumberInput
                            title='No. of Copies'
                            data={state.copies}
                            setData={(value) => dispatch({ type: 'SET_FIELD', field: 'copies', value })}
                            min={1}
                            step={1}
                            variant='stepper'
                            layout='inline'
                            nullAllowed={false}
                            onErrorSet={(value) => dispatch({ type: 'SET_ERROR', field: 'copies', error: value })}
                            errorText={state.errors.copies ? 'No. of Copies must be 1 or more' : ''}
                        />
                    </div>

                    <div className='request-form-section'>
                        <h2 className='request-form-section-title'>Print Options</h2>
                        <div className='repro-form-options-grid'>
                            <StandardRadioButtonSelector
                                title='Paper Size'
                                items={paperSizeOptions}
                                data={state.paperSize}
                                setData={(value) => dispatch({ type: 'SET_FIELD', field: 'paperSize', value })}
                            />
                            <StandardRadioButtonSelector
                                title='Sides'
                                items={sideOptions}
                                data={state.sides}
                                defaultValue='doubleSided'
                                setData={(value) => dispatch({ type: 'SET_FIELD', field: 'sides', value })}
                            />
                            <StandardRadioButtonSelector
                                title='Print Colour'
                                items={printColourOptions}
                                data={state.printColour}
                                setData={(value) => dispatch({ type: 'SET_FIELD', field: 'printColour', value })}
                            />
                            <StandardRadioButtonSelector
                                title='Material'
                                items={materialOptions}
                                data={state.material}
                                setData={(value) => dispatch({ type: 'SET_FIELD', field: 'material', value })}
                            />
                            <StandardRadioButtonSelector
                                title='Paper Colour'
                                items={paperColourOptions}
                                data={state.paperColour}
                                setData={(value) => dispatch({ type: 'SET_FIELD', field: 'paperColour', value })}
                            />
                        </div>
                    </div>

                    <div className='request-form-section'>
                        <h2 className='request-form-section-title'>Finishing</h2>
                        <div className='repro-form-options-grid repro-form-finishing-grid'>
                            <StandardRadioButtonSelector
                                title='Staples'
                                items={stapleOptions}
                                data={state.staples}
                                setData={(value) => dispatch({ type: 'SET_FIELD', field: 'staples', value })}
                            />
                            <StandardRadioButtonSelector
                                title='Binding'
                                items={bindingOptions}
                                data={state.binding}
                                setData={(value) => dispatch({ type: 'SET_FIELD', field: 'binding', value })}
                            />
                        </div>
                    </div>

                    <div className='request-form-section'>
                        <h2 className='request-form-section-title'>Files and Instructions</h2>
                        <p className='request-form-section-subheading'>
                            Uploading PDF files rather than Word documents or similar will result in easier processing for the user processing your request.
                        </p>
                        <FileDropzone
                            filesState={state.files}
                            fileStateSet={(files) => dispatch({ type: 'SET_FIELD', field: 'files', value: files })}
                            maxFiles={10}
                            maxTotalSizeMB={50}
                            enableClipboard
                            labelText='Files'
                            noteText='Files will not be uploaded until the request is submitted.'
                        />
                        <LinksComponent
                            resetTrigger={state.resetKey}
                            onChange={(cleanedLinks, rawLinks) => {
                                dispatch({ type: 'SET_FIELD', field: 'links', value: cleanedLinks });
                                dispatch({ type: 'SET_FIELD', field: 'rawLinks', value: rawLinks });

                                const hasInvalid = rawLinks?.some(link =>
                                    (link.name && link.name.trim() !== '' && (!link.url || link.url.trim() === '')) ||
                                    (link.url && link.url.trim() !== '' && (!link.name || link.name.trim() === ''))
                                );

                                if (hasInvalid) {
                                    dispatch({ type: 'SET_ERROR', field: 'links', error: 'Each link must include both a name and a URL.' });
                                } else {
                                    dispatch({ type: 'SET_ERROR', field: 'links', error: null });
                                }
                            }}
                        />
                        <StandardMultiLineTextEntry
                            title='Any Other Instruction'
                            data={state.otherInstructions}
                            setData={(value) => dispatch({ type: 'SET_FIELD', field: 'otherInstructions', value })}
                            errorText={state.errors.otherInstructions}
                        />
                    </div>

                    <SubmitButton text='Submit Request' isLoading={state.isSubmitting} loadingText={submitStatus || 'Submitting...'} />
                </form>
            </div>
        </div>
    );
}

/**
 * Displays the Reprographics request ID prominently after successful submission.
 */
function ReproSuccessModal({ active, requestId, message, onClose }) {
    if (!active) {
        return null;
    }

    return (
        <div className='repro-success-modal-overlay' role='dialog' aria-modal='true' aria-labelledby='repro-success-modal-title' onClick={onClose}>
            <div className='repro-success-modal' onClick={(event) => event.stopPropagation()}>
                <button className='repro-success-modal-close' type='button' aria-label='Close modal' onClick={onClose}>
                    ×
                </button>
                <h2 id='repro-success-modal-title' className='repro-success-modal-title'>Request Submitted</h2>
                <p className='repro-success-modal-id'>{requestId}</p>
                {message && <p className='repro-success-modal-text'>{message}</p>}
            </div>
        </div>
    );
}
